import { createPhoneSchema } from '@/components/FormFieldPhone';
import { ZIP_CODE_SCHEMA } from '@/utils/constant';
import { z } from 'zod';

// Name validation regex for person/entity names
const NAME_VALIDATION_REGEX = /^[a-zA-Z\s\-'.]+$/;
const NAME_VALIDATION_ERROR = 'Invalid name format';

// Step 1: Client Details Schema
// Step 1: Client Details Schema
const ClientDetailsBaseSchema = z.object({
  type: z.literal('DELIVERY', { message: 'Service type must be Delivery' }),
  clientId: z.string({ message: 'Client # is required' }).min(1, 'Client # is required'),
  clientName: z.string({ message: 'Client name is required' }),
  contactId: z.string({ message: 'Client contact is required' }).min(1, 'Client contact is required'),
  contactPhone: z.string().optional().nullable(),
  contactEmail: z.string().optional().nullable(),
  isPrepaid: z.boolean().default(false),
});

const ClientDetailsCaseNameFileRefSchema = z.object({
  caseName: z.string()
    .max(128, 'Case Name must be 128 characters or fewer')
    .optional()
    .nullable(),
  fileReferenceNumber: z.string()
    .max(20, 'File/Reference # must be 20 characters or fewer')
    .optional()
    .nullable(),
}).superRefine((data, ctx) => {
  // Either Case # OR File/Reference # must be present
  if (!data.caseName && !data.fileReferenceNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Either Case # or File/Reference # is required',
      path: ['caseName'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Either Case # or File/Reference # is required',
      path: ['fileReferenceNumber'],
    });
  }
});

export const DeliveryClientDetailsSchema = ClientDetailsBaseSchema.and(ClientDetailsCaseNameFileRefSchema);

// Step 2: Pickup Information Schema
export const DeliveryPickupInformationSchema = z.object({
  pickupPersonName: z
    .string({ message: 'Person/Entity name is required' })
    .min(1, 'Person/Entity name is required')
    .regex(NAME_VALIDATION_REGEX, NAME_VALIDATION_ERROR),
  pickupPhone: createPhoneSchema(false),
  pickupAddressSameAsClient: z.boolean().default(true),
  pickupAddressLine1: z.string({ message: 'Address Line 1 is required' }).min(1, 'Address Line 1 is required').max(100, 'Address Line 1 must be 100 characters or fewer'),
  pickupAddressLine2: z.string().max(100, 'Address Line 2 must be 100 characters or fewer').optional().nullable(),
  pickupCity: z.string({ message: 'City is required' }).min(1, 'City is required').max(50, 'City must be 50 characters or fewer').regex(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
  pickupCounty: z.string({ message: 'County is required' }).min(1, 'County is required'),
  pickupState: z.string({ message: 'State is required' }).min(2, 'State is required').max(2, 'State is invalid').regex(/^[a-zA-Z]+$/, 'State can only contain letters').transform(val => val.toUpperCase()),
  pickupZip: ZIP_CODE_SCHEMA,
  pickupDate: z.date({ message: 'Pickup date is required' }),
  pickupType: z.enum(['ENVELOPE', 'BOX', 'OTHER'], { message: 'Type of Pickup is required' }),
  numberOfItems: z.coerce
    .number({ message: 'Number of Items is required' })
    .int()
    .positive('Must be a positive number'),
  timeReady: z.string({ message: 'Time Ready is required' }).regex(/^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i, {
    message: 'Time must be in HH:MM AM/PM format',
  }),
  additionalJobs: z
    .array(
      z.discriminatedUnion('type', [
        z.object({
          type: z.literal('PRINTING'),
          numberOfPages: z
            .coerce.number({ message: 'Number of Pages is required for printing jobs' })
            .int()
            .positive('Number of Pages must be a positive number'),
        }),
        z.object({
          type: z.literal('OTHER'),
          numberOfPages: z.coerce.number().int().positive().optional(),
        }),
      ])
    )
    .optional(),
  assignedPickupDriverId: z.string().optional().nullable(),
  pickupDriverPhone: z.string().optional().nullable(),
  pickupInstructions: z.string().optional().nullable(),
});

// Step 3: Delivery Information Schema (per delivery)
export const DeliveryInformationItemSchema = z.object({
  id: z.string(),
  personEntityName: z
    .string({ message: 'Person/Entity name is required' })
    .min(1, 'Person/Entity name is required')
    .regex(NAME_VALIDATION_REGEX, NAME_VALIDATION_ERROR),
  attention: z.string().regex(/^[a-zA-Z\s\-'.]*$/, 'Invalid name format').optional().nullable(),
  phone: createPhoneSchema(false),
  copyAddressFromClient: z.boolean().default(false),
  deliveryAddressLine1: z
    .string({ message: 'Delivery Address Line 1 is required' })
    .min(1, 'Delivery Address Line 1 is required')
    .max(100, 'Address Line 1 must be 100 characters or fewer'),
  deliveryAddressLine2: z.string().max(100, 'Address Line 2 must be 100 characters or fewer').optional().nullable(),
  deliveryCity: z.string({ message: 'City is required' }).min(1, 'City is required').max(50, 'City must be 50 characters or fewer').regex(/^[a-zA-Z\s'-]+$/, 'City can only contain letters, spaces, hyphens, and apostrophes'),
  deliveryCounty: z.string({ message: 'County is required' }).min(1, 'County is required'),
  deliveryState: z.string({ message: 'State is required' }).min(2, 'State is required').max(2, 'State is invalid').regex(/^[a-zA-Z]+$/, 'State can only contain letters').transform(val => val.toUpperCase()),
  deliveryZip: ZIP_CODE_SCHEMA,
  deliveryDate: z.date({ message: 'Delivery date is required' }),
  deliveryTime: z.string().min(1, 'Delivery Time is required').regex(/^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i, {
    message: 'Time must be in HH:MM AM/PM format',
  }),
  assignedDeliveryDriverId: z.string().optional().nullable(),
  deliveryDriverPhone: z.string().optional().nullable(),
  additionalDriverIds: z.array(z.string()).optional(),
  returnDate: z.date().optional().nullable(),
  hasReturn: z.boolean().optional(),
  deliveryInstructions: z.string({ message: 'Delivery instructions are required' }).min(1, 'Instructions required'),
  hasDgrCheck: z.boolean().default(false),
  checkNumber: z.string().max(20, 'Check # must be 20 characters or fewer').regex(/^[0-9]*$/, 'Check # must contain only numbers').optional().nullable(),
  checkAmount: z.coerce.number().optional().nullable(),
});

export const buildDeliveryInformationSchema = (options?: { allowPastDeliveryDate?: boolean }) =>
  z
    .object({
      pickupDate: z.date().optional(),
      deliveries: z
        .array(DeliveryInformationItemSchema)
        .min(1, 'At least one delivery is required')
        .max(50, 'Maximum 50 deliveries allowed'),
    })
    .superRefine((data, ctx) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const pickupDate = data.pickupDate ? new Date(data.pickupDate) : null;
      if (pickupDate) {
        pickupDate.setHours(0, 0, 0, 0);
      }

      data.deliveries.forEach((delivery, index) => {
        if (delivery.deliveryDate) {
          const deliveryDate = new Date(delivery.deliveryDate);
          deliveryDate.setHours(0, 0, 0, 0);

          // DS41: Delivery date cannot be in the past (not enforced when editing an existing work order)
          if (!options?.allowPastDeliveryDate && deliveryDate < today) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Delivery date cannot be in the past',
              path: [`deliveries.${index}.deliveryDate`],
            });
          }

          if (pickupDate && deliveryDate < pickupDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Delivery date cannot be before pickup date',
              path: [`deliveries.${index}.deliveryDate`],
            });
          }
        }

        // If DGR Check is selected, Check Number and Check Amount are required
        if (delivery.hasDgrCheck) {
          if (!delivery.checkNumber) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Check Number is required when DGR Check is selected',
              path: [`deliveries.${index}.checkNumber`],
            });
          }
          if (!delivery.checkAmount || delivery.checkAmount <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Check Amount is required when DGR Check is selected',
              path: [`deliveries.${index}.checkAmount`],
            });
          }
        }

        const primaryDriverId = delivery.assignedDeliveryDriverId ? String(delivery.assignedDeliveryDriverId) : '';
        const additionalDriverIds = (delivery.additionalDriverIds || []).map((id) => String(id || '')).filter(Boolean);

        if (primaryDriverId && additionalDriverIds.includes(primaryDriverId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Primary driver cannot also be selected as an additional driver',
            path: [`deliveries.${index}.additionalDriverIds`],
          });
        }

        if (new Set(additionalDriverIds).size !== additionalDriverIds.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Each additional driver must be unique',
            path: [`deliveries.${index}.additionalDriverIds`],
          });
        }
      });
    });

// isEditMode: when editing an existing work order, the delivery date may already
// be in the past relative to today and must remain editable/saveable in that case.
export const createDeliveryInformationSchema = (isEditMode = false) =>
  buildDeliveryInformationSchema({ allowPastDeliveryDate: isEditMode });

export const DeliveryInformationSchema = buildDeliveryInformationSchema();

// Step 4: Billing Schema (per delivery)
export const DeliveryBillingItemSchema = z.object({
  baseCharge: z.coerce.number({ message: 'Base charge is required' }),
  fuelSurcharge: z.coerce.number({ message: 'Fuel surcharge is required' }),
  boxFee: z.coerce.number().optional(),
  copyCharges: z.coerce.number().optional(),
  returnAmount: z.coerce.number().optional(),
  checkAdvancementFee: z.coerce.number().optional(),
  miscFee: z.coerce.number().optional(),
  totalBillingFee: z.coerce.number({ message: 'Total billing fee is required' }),
});

export const DeliveryBillingSchema = z.object({
  deliveryBillings: z.array(DeliveryBillingItemSchema).min(1, 'At least one billing entry is required'),
});

// Complete Delivery Form Schema (combines all steps with cross-field validations)
export const buildDeliveryFormSchema = (options?: { allowPastDeliveryDate?: boolean }) =>
  z
    .intersection(
      DeliveryClientDetailsSchema,
      z.intersection(
        DeliveryPickupInformationSchema,
        z.intersection(buildDeliveryInformationSchema(options), DeliveryBillingSchema)
      )
    )
    .superRefine((data, ctx) => {
      // Pickup Date <= Delivery Date validation
      data.deliveries.forEach((delivery, index) => {
        if (data.pickupDate && delivery.deliveryDate) {
          const pDate = new Date(data.pickupDate);
          pDate.setHours(0, 0, 0, 0);
          const dDate = new Date(delivery.deliveryDate);
          dDate.setHours(0, 0, 0, 0);

          if (pDate > dDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Delivery date cannot be before pickup date',
              path: [`deliveries.${index}.deliveryDate`],
            });
          }
        }

        // Return Date >= Delivery Date
        if (delivery.returnDate && delivery.deliveryDate) {
          const rDate = new Date(delivery.returnDate);
          rDate.setHours(0, 0, 0, 0);
          const dDate = new Date(delivery.deliveryDate);
          dDate.setHours(0, 0, 0, 0);

          if (rDate < dDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Return date cannot be before delivery date',
              path: [`deliveries.${index}.returnDate`],
            });
          }
        }
      });

      // Pickup Address AND Delivery Address cannot BOTH match Client Address
      // This validation would require client address data, implement in component logic
    });

export const createDeliveryFormSchema = (isEditMode = false) =>
  buildDeliveryFormSchema({ allowPastDeliveryDate: isEditMode });

export const DeliveryFormSchema = buildDeliveryFormSchema();

// Type inference
export type DeliveryClientDetailsFormData = z.infer<typeof DeliveryClientDetailsSchema>;
export type DeliveryPickupInformationFormData = z.infer<typeof DeliveryPickupInformationSchema>;
export type DeliveryInformationFormData = z.infer<typeof DeliveryInformationSchema>;
export type DeliveryBillingFormData = z.infer<typeof DeliveryBillingSchema>;
export type DeliveryFormData = z.infer<typeof DeliveryFormSchema>;
